// src/services/pushNotificationService.js

import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/push`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/**
 * Convertit la clé publique VAPID base64 URL-safe
 * en Uint8Array, format attendu par pushManager.subscribe().
 */
const urlBase64ToUint8Array = (base64String = "") => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);

  const base64 = `${base64String}${padding}`
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);

  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

/**
 * Détecte rapidement le type d'appareil.
 */
export const getPushPlatform = () => {
  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";

  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const isAndroid = /Android/i.test(userAgent);

  if (isIOS) return "ios";
  if (isAndroid) return "android";

  return "desktop";
};

/**
 * Vérifie si l'application est lancée comme PWA installée.
 * Utile surtout pour iPhone.
 */
export const isStandalonePwa = () => {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
};

/**
 * Vérifie si le navigateur supporte les notifications push.
 */
export const isPushSupported = () => {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
};

/**
 * Récupère la permission actuelle du navigateur.
 * Valeurs possibles : default, granted, denied.
 */
export const getNotificationPermission = () => {
  if (!("Notification" in window)) {
    return "unsupported";
  }

  return Notification.permission;
};

/**
 * Récupère la clé publique VAPID côté serveur.
 */
export const getVapidPublicKey = async () => {
  try {
    const response = await axios.get(`${apiUrl}/vapid-public-key`);

    return response.data?.publicKey || null;
  } catch (error) {
    console.error("Erreur lors du chargement de la clé VAPID:", error);
    return null;
  }
};

/**
 * Enregistre le service worker.
 * Le fichier devra être placé dans public/service-worker.js
 */
export const registerPushServiceWorker = async () => {
  try {
    if (!("serviceWorker" in navigator)) {
      throw new Error("Service Worker non supporté par ce navigateur.");
    }

    const registration = await navigator.serviceWorker.register(
      "/service-worker.js"
    );

    return registration;
  } catch (error) {
    console.error("Erreur lors de l'enregistrement du service worker:", error);
    return null;
  }
};

/**
 * Récupère l'état push côté serveur.
 */
export const getPushStatus = async () => {
  try {
    const response = await axios.get(`${apiUrl}/status`, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement du statut push:", error);

    return {
      push_notifications_enabled: false,
      active_subscriptions_count: 0,
      has_active_subscription: false,
    };
  }
};

/**
 * Récupère la subscription existante sur ce navigateur.
 */
export const getExistingPushSubscription = async () => {
  try {
    if (!isPushSupported()) {
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    return subscription;
  } catch (error) {
    console.error("Erreur lors de la récupération de la subscription:", error);
    return null;
  }
};

/**
 * Demande l'autorisation navigateur.
 */
export const requestNotificationPermission = async () => {
  try {
    if (!("Notification" in window)) {
      return {
        granted: false,
        permission: "unsupported",
        message: "Les notifications ne sont pas supportées sur ce navigateur.",
      };
    }

    const permission = await Notification.requestPermission();

    return {
      granted: permission === "granted",
      permission,
      message:
        permission === "granted"
          ? "Notifications autorisées."
          : "Autorisation de notification refusée.",
    };
  } catch (error) {
    console.error("Erreur lors de la demande d'autorisation push:", error);

    return {
      granted: false,
      permission: "error",
      message: "Impossible de demander l'autorisation de notification.",
    };
  }
};

/**
 * Active les notifications push sur cet appareil.
 */
export const subscribeToPushNotifications = async () => {
  try {
    if (!isPushSupported()) {
      return {
        success: false,
        message: "Les notifications push ne sont pas supportées ici.",
      };
    }

    const permissionResult = await requestNotificationPermission();

    if (!permissionResult.granted) {
      return {
        success: false,
        message: permissionResult.message,
        permission: permissionResult.permission,
      };
    }

    const vapidPublicKey = await getVapidPublicKey();

    if (!vapidPublicKey) {
      return {
        success: false,
        message: "Clé VAPID publique introuvable.",
      };
    }

    const registration = await registerPushServiceWorker();

    if (!registration) {
      return {
        success: false,
        message: "Service worker non enregistré.",
      };
    }

    const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
      });
    }

    const response = await axios.post(
      `${apiUrl}/subscribe`,
      {
        subscription,
        platform: getPushPlatform(),
      },
      {
        headers: getAuthHeaders(),
      }
    );

    return {
      success: true,
      ...response.data,
    };
  } catch (error) {
    console.error("Erreur lors de l'activation des notifications push:", error);

    return {
      success: false,
      message:
        error?.response?.data?.message ||
        error?.message ||
        "Erreur lors de l'activation des notifications push.",
    };
  }
};

/**
 * Désactive les notifications push sur cet appareil uniquement.
 */
export const unsubscribeFromPushNotifications = async () => {
  try {
    if (!isPushSupported()) {
      return {
        success: false,
        message: "Les notifications push ne sont pas supportées ici.",
      };
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return {
        success: true,
        message: "Aucune notification push active sur cet appareil.",
        push_notifications_enabled: false,
      };
    }

    const endpoint = subscription.endpoint;

    await subscription.unsubscribe();

    const response = await axios.post(
      `${apiUrl}/unsubscribe`,
      {
        endpoint,
      },
      {
        headers: getAuthHeaders(),
      }
    );

    return {
      success: true,
      ...response.data,
    };
  } catch (error) {
    console.error(
      "Erreur lors de la désactivation des notifications push:",
      error
    );

    return {
      success: false,
      message:
        error?.response?.data?.message ||
        error?.message ||
        "Erreur lors de la désactivation des notifications push.",
    };
  }
};

/**
 * Désactive toutes les subscriptions de l'utilisateur côté back.
 */
export const disableAllPushNotifications = async () => {
  try {
    const response = await axios.post(
      `${apiUrl}/disable-all`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );

    return {
      success: true,
      ...response.data,
    };
  } catch (error) {
    console.error(
      "Erreur lors de la désactivation globale des notifications push:",
      error
    );

    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Erreur lors de la désactivation globale des notifications push.",
    };
  }
};

/**
 * Envoie une notification push de test à l'utilisateur connecté.
 */
export const sendTestPushNotification = async () => {
  try {
    const response = await axios.post(
      `${apiUrl}/test`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );

    return {
      success: true,
      ...response.data,
    };
  } catch (error) {
    console.error("Erreur lors de l'envoi du push de test:", error);

    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Erreur lors de l'envoi du push de test.",
    };
  }
};